/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState, useCallback } from "react";
import { TouchableOpacity, View } from "react-native";
import FacebookLogin, {
  ReactFacebookLoginInfo,
  ReactFacebookFailureResponse,
} from "react-facebook-login";
import { TokenResponse, useGoogleLogin } from "@react-oauth/google";
import { Redirect, useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { Button, LoadingView, TermsConditionsText, Text } from "UpduoLibrary";
import {
  skalePrimaryBlue,
  skaleSecondaryBlue,
  skaleRed,
  skaleWebTextLightGray,
  skaleWhite,
  skaleGreen,
  skaleBackgroundGreen,
  salesforceBlue,
  colorWithOpacity,
} from "config/UpduoColors";
import UpduoLogo from "assets/upduo-black.svg";
import SalesforceLogo from "assets/salesforce.svg";
import SignupForm from "./SignupForm";
import LoginForm from "./LoginForm";
interface SignupLoginViewProps {
  onSignupPress?: Function;
  onLoginPress?: Function;
  onFacebookPress?: (
    response: ReactFacebookLoginInfo | ReactFacebookFailureResponse
  ) => void;
  onGooglePress?: (response: TokenResponse) => void;
  onSalesforcePress?: Function;
  loginValidationText?: string;
  isLoginInProgress?: boolean;
  emailValidationText?: string;
  passwordValidationText?: string;
}

export default function SignupLoginView({
  onSignupPress,
  onLoginPress,
  onFacebookPress,
  onGooglePress,
  onSalesforcePress,
  loginValidationText,
  isLoginInProgress,
  emailValidationText,
  passwordValidationText,
}: SignupLoginViewProps) {
  const primaryAuthToken = useSelector(
    (state: RootState) => state.primaryAuthToken
  );
  const authToken = useSelector((state: RootState) => state.authToken);
  const startObject = useSelector((state: RootState) => state.startObject);

  const location = useLocation() as any; // TODO fix router types

  const [email, setEmail] = useState(startObject.email || "");
  const [password, setPassword] = useState("");
  const [showLoginValidationText, setShowLoginValidationText] = useState(true);

  const [firstName, setFirstName] = useState(startObject.firstName || "");
  const [lastName, setLastName] = useState(startObject.lastName || "");
  const [emailValid, setEmailValid] = useState(true);
  const [showEmailValidationText, setShowEmailValidationText] = useState(true);
  const [passwordValid, setPasswordValid] = useState(false);
  const [passwordServerValid, setPasswordServerValid] = useState(true);
  const [signupInvalid, setSignupInvalid] = useState(true);

  const [shownView, setShownView] = useState(
    onSignupPress ? "signup" : "login"
  );

  const doGoogleLogin = useGoogleLogin({
    onSuccess: (tokenResponse: TokenResponse) =>
      onGooglePress ? onGooglePress(tokenResponse) : null,
    onError: (error) => {
      console.log("useGoogleLogin error", error);
    },
  });

  useEffect(() => {
    setFirstName(startObject.firstName || "");
    setLastName(startObject.lastName || "");
    setEmail(startObject.email || "");
  }, [startObject]);

  useEffect(() => {
    // Disable button if fields are not validated
    setSignupInvalid(
      !passwordValid ||
        !emailValid ||
        firstName.length === 0 ||
        lastName.length === 0
    );
  }, [passwordValid, emailValid, firstName, lastName]);

  useEffect(() => {
    // Validate email address field
    const regexPattern = /.+@.+\..+/g;
    setEmailValid(regexPattern.test(email));
  }, [email]);

  useEffect(() => {
    // Validate password field
    setPasswordValid(password.length >= 10);
    setPasswordServerValid(true);
  }, [password]);

  useEffect(() => {
    if (loginValidationText) {
      setShowLoginValidationText(true);
      setShownView("login");
    }
  }, [loginValidationText]);

  useEffect(() => {
    if (emailValidationText) {
      setShowEmailValidationText(true);
    }
  }, [emailValidationText]);

  useEffect(() => {
    if (passwordValidationText) {
      setPasswordServerValid(false);
    }
  }, [passwordValidationText]);

  const _handleEmailChangeText = (text: string) => {
    setEmail(text);
    setShowEmailValidationText(true);
    setShowLoginValidationText(false);
  };

  const _handlePasswordChangeText = (text: string) => {
    setPassword(text);
    setShowLoginValidationText(false);
  };

  const _handleButtonPress = useCallback(() => {
    if (shownView === "login" && onLoginPress) {
      onLoginPress(email, password);
    } else if (onSignupPress) {
      onSignupPress(firstName, lastName, email, password);
    }
  }, [
    shownView,
    onLoginPress,
    onSignupPress,
    firstName,
    lastName,
    email,
    password,
  ]);

  const _handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        _handleButtonPress();
      }
    },
    [_handleButtonPress]
  );

  const rsvpBanner = (
    <View
      style={{
        width: 250,
        paddingVertical: 10,
        paddingHorizontal: 20,
        marginBottom: 30,
        backgroundColor: colorWithOpacity(skaleBackgroundGreen, 0.15),
        flexDirection: "row",
        borderRadius: 8,
        alignSelf: "center",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Text style={{ fontSize: 16, color: skaleGreen }}>
        {`You can RSVP by ${
          shownView === "login" ? "logging in" : "signing up"
        }!`}
      </Text>
    </View>
  );

  if (shownView === "loading") {
    return <LoadingView />;
  }
  if (primaryAuthToken && authToken) {
    if (
      location.state?.referrer?.pathname &&
      location.state?.referrer?.redirect
    ) {
      if (location.state.referrer.rsvpRequest) {
        return (
          <Redirect
            to={{
              pathname: location.state.referrer.pathname,
              search: "?rsvpSuccess=true",
            }}
          />
        );
      } else return <Redirect to={location.state.referrer.pathname} />;
    } else return <Redirect to={{ pathname: "/" }} />;
  } else if (isLoginInProgress || primaryAuthToken) {
    return <LoadingView />;
  }
  if (shownView === "salesforce") {
    return (
      <View style={{ flex: 1, alignItems: "center", padding: 50 }}>
        <img
          src={UpduoLogo}
          style={{ width: isMobile ? 43 : 53 }}
          alt={"logo"}
        />
        <Text
          isHeading={true}
          style={{ padding: 20, color: skaleSecondaryBlue }}
        >
          {"Redirecting to Salesforce login..."}
        </Text>
      </View>
    );
  }
  return (
    <View
      style={{
        flexGrow: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: skaleWhite,
        paddingHorizontal: 30,
        paddingVertical: 30,
      }}
    >
      <View style={{ justifyContent: "center" }}>
        {location.state?.referrer?.rsvpRequest ? rsvpBanner : null}
        {shownView === "login" ? (
          <LoginForm
            email={email}
            password={password}
            startObject={startObject}
            onChangeTextEmail={(text: string) => _handleEmailChangeText(text)}
            onKeyPressEmail={(e) => _handleKeyPress(e)}
            onChangeTextPassword={(text: string) =>
              _handlePasswordChangeText(text)
            }
            onKeyPressPassword={(e) => _handleKeyPress(e)}
          />
        ) : (
          <SignupForm
            firstName={firstName}
            lastName={lastName}
            email={email}
            emailValid={emailValid}
            showEmailValidationText={showEmailValidationText}
            emailValidationText={emailValidationText}
            password={password}
            passwordValid={passwordValid}
            passwordValidationText={passwordValidationText}
            passwordServerValid={passwordServerValid}
            startObject={startObject}
            onChangeTextFirstName={(text: string) => setFirstName(text)}
            onKeyPressFirstName={(e) => _handleKeyPress(e)}
            onChangeTextLastName={(text: string) => setLastName(text)}
            onKeyPressLastName={(e) => _handleKeyPress(e)}
            onChangeTextEmail={(text: string) => _handleEmailChangeText(text)}
            onKeyPressEmail={(e) => _handleKeyPress(e)}
            onChangeTextPassword={(text: string) =>
              _handlePasswordChangeText(text)
            }
            onKeyPressPassword={(e) => _handleKeyPress(e)}
          />
        )}
      </View>
      {shownView === "login" &&
      loginValidationText &&
      showLoginValidationText ? (
        <Text
          style={{
            color: skaleRed,
            fontSize: 12,
            marginTop: 10,
            marginBottom: -10,
          }}
        >
          {loginValidationText}
        </Text>
      ) : null}
      <TermsConditionsText />
      <View style={{ alignSelf: "center", alignItems: "center" }}>
        <Button
          onPress={_handleButtonPress}
          text={shownView === "login" ? "Log In" : "Sign Up"}
          disabled={shownView === "signup" && signupInvalid}
        />
        {startObject.orgJoinRule !== "O" &&
        (onFacebookPress || onGooglePress || onSalesforcePress) ? (
          <>
            <Text style={{ color: skaleWebTextLightGray, marginVertical: 30 }}>
              {"OR"}
            </Text>
            <View style={{ flexDirection: "row", height: 45 }}>
              {onFacebookPress ? (
                <FacebookLogin
                  buttonStyle={{
                    fontSize: 12,
                    paddingTop: 6,
                    paddingBottom: 6,
                    paddingLeft: 0,
                    paddingRight: 0,
                    marginBottom: 15,
                    width: 145,
                    height: 45,
                    marginRight: 20,
                  }}
                  appId={
                    process.env.REACT_APP_FACEBOOK_KEY
                      ? process.env.REACT_APP_FACEBOOK_KEY
                      : ""
                  }
                  callback={onFacebookPress}
                  textButton={"Facebook Login"}
                />
              ) : null}
              {onGooglePress ? (
                <TouchableOpacity
                  style={{
                    flexDirection: "row",
                    borderWidth: 1,
                    borderRadius: 10,
                    borderColor: skaleWebTextLightGray,
                    alignItems: "center",
                    paddingLeft: 10,
                    paddingRight: 15,
                  }}
                  onPress={() => doGoogleLogin()}
                >
                  <img
                    src={
                      "https://developers.google.com/identity/images/g-logo.png"
                    }
                    style={{
                      width: 30,
                      height: 30,
                      marginRight: 10,
                    }}
                    alt={"google"}
                  />
                  <Text>{"Google Login"}</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            {onSalesforcePress ? (
              <TouchableOpacity
                onPress={() => {
                  onSalesforcePress();
                  setShownView("salesforce");
                }}
                style={{
                  width: 145,
                  height: 45,
                  marginTop: 20,
                  backgroundColor: salesforceBlue,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 5,
                }}
              >
                <img
                  src={SalesforceLogo}
                  style={{
                    width: 85,
                    marginTop: 5,
                    marginLeft: -9,
                    marginRight: -3,
                  }}
                  alt={"salesforce"}
                />
                <Text style={{ color: skaleWhite, marginBottom: 1 }}>
                  {"Login"}
                </Text>
              </TouchableOpacity>
            ) : null}
          </>
        ) : null}
      </View>
      {onSignupPress && onLoginPress ? (
        <View style={{ marginTop: 30, alignSelf: "center" }}>
          <Text style={{ fontSize: 16, color: skaleWebTextLightGray }}>
            {shownView === "login"
              ? "Don't have an account?   "
              : "Already have an account?   "}
            <TouchableOpacity
              onPress={() => {
                setShownView((previousState) =>
                  previousState === "signup" ? "login" : "signup"
                );
              }}
            >
              <Text
                isHeading={true}
                style={{
                  fontSize: 16,
                  color: skalePrimaryBlue,
                }}
              >
                {shownView === "login" ? "SIGN UP" : "LOGIN"}
              </Text>
            </TouchableOpacity>
          </Text>
        </View>
      ) : null}
    </View>
  );
}
